import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Calendar, PartyPopper, Trash2, Edit } from "lucide-react";

export default function AdminFestivals() {
  const [list, setList] = useState([]);
  const [menu, setMenu] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/festivals").then(r => setList(r.data));
    api.get("/menu").then(r => setMenu(r.data));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/festivals/${editing.id}`, editing);
      else await api.post("/festivals", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/festivals/${id}`); load();
  };
  const toggleMenu = (mid) => {
    const ids = editing.special_menu_ids || [];
    setEditing({ ...editing, special_menu_ids: ids.includes(mid) ? ids.filter(x=>x!==mid) : [...ids, mid] });
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Festivals & Events</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Special menus for special days</h1>
          </div>
          <button data-testid="new-fest-btn" onClick={() => setEditing({ name:"", start_date: new Date().toISOString().slice(0,10), end_date: new Date().toISOString().slice(0,10), description:"", banner_url:"", special_menu_ids: [], status: "upcoming" })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold">
            <Plus size={16} /> New festival
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map(f => (
            <div key={f.id} className="bg-white rounded-3xl border border-border overflow-hidden" data-testid={`fest-${f.id}`}>
              {f.banner_url && <div className="h-32 bg-cream-200 overflow-hidden"><img src={f.banner_url} alt={f.name} className="w-full h-full object-cover" /></div>}
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <PartyPopper className="text-brand" size={18} />
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${f.status==='active'?'bg-emerald-100 text-emerald-900':f.status==='upcoming'?'bg-amber-100 text-amber-900':'bg-cream-300 text-ink-700'}`}>{f.status}</span>
                </div>
                <div className="font-heading text-xl font-bold text-ink mt-2">{f.name}</div>
                <div className="text-xs text-ink-500 mt-1 flex items-center gap-1"><Calendar size={12} />{f.start_date} → {f.end_date}</div>
                <p className="text-sm text-ink-600 mt-3 line-clamp-2">{f.description}</p>
                <div className="text-xs text-ink-500 mt-2">{(f.special_menu_ids || []).length} special items</div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`fest-edit-${f.id}`} onClick={() => setEditing(f)} className="px-3 py-1.5 rounded-full bg-cream-200 text-xs font-semibold flex items-center gap-1"><Edit size={12} /> Edit</button>
                  <button onClick={() => remove(f.id)} className="px-3 py-1.5 rounded-full bg-red-50 text-brand text-xs font-semibold flex items-center gap-1"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 py-8 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">{editing.id ? "Edit" : "New"} festival</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Name</label>
                <input data-testid="fest-name" value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Start</label>
                <input data-testid="fest-start" type="date" value={editing.start_date} onChange={e => setEditing({...editing, start_date: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">End</label>
                <input data-testid="fest-end" type="date" value={editing.end_date} onChange={e => setEditing({...editing, end_date: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Banner URL</label>
                <input data-testid="fest-banner" value={editing.banner_url} onChange={e => setEditing({...editing, banner_url: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Description</label>
                <textarea data-testid="fest-desc" rows={2} value={editing.description} onChange={e => setEditing({...editing, description: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Status</label>
                <select data-testid="fest-status" value={editing.status} onChange={e => setEditing({...editing, status: e.target.value})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none">
                  {["upcoming","active","ended"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Special menu items</label>
                <div className="mt-2 max-h-48 overflow-y-auto grid grid-cols-2 gap-2">
                  {menu.map(m => {
                    const active = editing.special_menu_ids?.includes(m.id);
                    return (
                      <button key={m.id} type="button" data-testid={`fest-pick-${m.id}`} onClick={() => toggleMenu(m.id)}
                        className={`text-left p-2 rounded-lg text-xs border ${active ? 'bg-brand text-white border-brand' : 'bg-white border-border'}`}>
                        {m.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="fest-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
